import { registerLocaleData } from "@angular/common";
import localeFr from "@angular/common/locales/fr";
import { ApplicationConfig, LOCALE_ID, importProvidersFrom, provideZoneChangeDetection } from "@angular/core";
import { provideRouter, withPreloading, NoPreloading } from "@angular/router";
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { RouteReuseStrategy } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { provideIonicAngular, IonicRouteStrategy } from "@ionic/angular/standalone";
import { AngularDelegate, IonicModule, ModalController, PopoverController } from "@ionic/angular";
import { provideStore } from "@ngrx/store";
import { provideRouterStore, routerReducer } from "@ngrx/router-store";
import { TranslateLoader } from "@ngx-translate/core";
import { provideTranslateService } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { Capacitor } from "@capacitor/core";
import { IonicStorageModule } from "@ionic/storage-angular";
import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import { provideNativeDateAdapter } from "@angular/material/core";
import { getApp, initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { browserLocalPersistence, getAuth, initializeAuth, inMemoryPersistence, provideAuth } from "@angular/fire/auth";
import { getDatabase, provideDatabase } from "@angular/fire/database";
import { getFunctions, provideFunctions } from "@angular/fire/functions";
import { initializeFirestore, memoryLocalCache, provideFirestore } from "@angular/fire/firestore";
import { getStorage, provideStorage } from "@angular/fire/storage";
import { LogLevel, setLogLevel } from "@angular/fire";

import { routes } from "./app.routes";
import { enterAnimation } from "./shared/animations/routing-animation";
import { environment } from "../environments/environment";
import {
	chatsReducers,
	containersReducers,
	eventsReducers,
	genericsModulesDataReducers,
	modulesReducers,
	utilityReducers
} from "./shared/reducers";
import { authReducers } from "./shared/reducers/auth.reducers";
import {
	AuthGuard,
	EventBlockedGuard,
	EventGuard,
	EventsListGuard,
	LoginGuard,
	ProfileEditGuard,
	RegisterFormGuard,
	ShortcodeGuard
} from "./shared/guards";
import {
	AnalyticsService,
	AppointmentsService,
	AuthService,
	CardExchangeService,
	ChatsService,
	CheckinsService,
	CustomFieldsService,
	CustomPagesService,
	DatabaseService,
	DeviceInfoService,
	DocumentsService,
	EventUsersService,
	EventsService,
	ExternalInteractivityService,
	FeedNewsService,
	FeedbacksService,
	FormsService,
	HeaderService,
	InteractivityService,
	LanguagesService,
	LastAccessService,
	LocationsService,
	LuxonService,
	ModulesManagerService,
	MongodbService,
	NavigationServices,
	NoteTakingService,
	PointsService,
	QuizsService,
	RankingsService,
	ReportsService,
	ResultsService,
	SchedulesService,
	SheetsService,
	StateManagerService,
	StorageService,
	SurveysService,
	TracksService,
	TreasureHuntsService,
	UtilityService,
	VisiosService,
	WordCloudsService
} from "./shared/services";

setLogLevel(LogLevel.SILENT);
registerLocaleData(localeFr);

export function createTranslateLoader(http: HttpClient): TranslateHttpLoader {
	return new TranslateHttpLoader(http, "../assets/i18n/", ".json");
}

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(routes, withPreloading(NoPreloading)),
		provideHttpClient(withInterceptorsFromDi()),
		provideAnimations(),
		provideIonicAngular({
			navAnimation: enterAnimation,
			swipeBackEnabled: false
		}),
		AngularDelegate,
		ModalController,
		PopoverController,
		provideStore({
			auth: authReducers,
			chats: chatsReducers,
			containers: containersReducers,
			events: eventsReducers,
			genericsModulesData: genericsModulesDataReducers,
			modules: modulesReducers,
			router: routerReducer,
			utility: utilityReducers
		}),
		provideRouterStore(),
		provideTranslateService({
			loader: {
				provide: TranslateLoader,
				useFactory: createTranslateLoader,
				deps: [HttpClient]
			}
		}),
		provideFirebaseApp(() => {
			return initializeApp(environment.firebase);
		}),
		provideAuth(() => {
			if (Capacitor.isNativePlatform()) {
				try {
					return initializeAuth(getApp(), {
						persistence: [browserLocalPersistence, inMemoryPersistence]
					});
				} catch (error) {
					return initializeAuth(getApp(), {
						persistence: inMemoryPersistence
					});
				}
			} else {
				return getAuth();
			}
		}),
		provideFirestore(() => {
			const isNative = Capacitor.isNativePlatform();
			if (isNative) {
				return initializeFirestore(getApp(), {
					experimentalForceLongPolling: true
				});
			} else {
				return initializeFirestore(getApp(), {});
			}
		}),
		provideDatabase(() => getDatabase()),
		provideStorage(() => getStorage()),
		provideFunctions(() => {
			return getFunctions(getApp(), environment.platform.region);
		}),
		provideServiceWorker("service-worker.js", {
			enabled: environment.production
		}),
		importProvidersFrom(IonicStorageModule.forRoot()),
		importProvidersFrom(IonicModule.forRoot({})),
		{ provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
		{ provide: LOCALE_ID, useValue: "fr-FR" },
		{ provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true } },
		provideNativeDateAdapter(),
		AuthGuard,
		EventBlockedGuard,
		EventGuard,
		RegisterFormGuard,
		EventsListGuard,
		LoginGuard,
		ProfileEditGuard,
		ShortcodeGuard,
		AnalyticsService,
		AppointmentsService,
		AuthService,
		CardExchangeService,
		ChatsService,
		CheckinsService,
		CustomFieldsService,
		CustomPagesService,
		DatabaseService,
		DeviceInfoService,
		DocumentsService,
		EventsService,
		EventUsersService,
		ExternalInteractivityService,
		FeedbacksService,
		FormsService,
		HeaderService,
		InteractivityService,
		LanguagesService,
		LastAccessService,
		LocationsService,
		LuxonService,
		ModulesManagerService,
		MongodbService,
		NavigationServices,
		NoteTakingService,
		FeedNewsService,
		PointsService,
		QuizsService,
		RankingsService,
		ReportsService,
		ResultsService,
		SchedulesService,
		SheetsService,
		StateManagerService,
		StorageService,
		SurveysService,
		TracksService,
		TreasureHuntsService,
		UtilityService,
		VisiosService,
		WordCloudsService
	]
};
